// Shared utilities for Appointments scheduler
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { formatDateWithPreferences, formatTimeWithPreferences } from 'utils/dateFormatUtils';

export function toUtcIso(date) {
  // Accepts a Date or string; returns UTC ISO string
  return new Date(date).toISOString();
}

export function genId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_) { /* noop */ }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function fromLocalDateTime(date, hhmm, tz) {
  // Build a UTC instant corresponding to clinic-local date+time
  const [h, m] = (typeof hhmm === 'string' && hhmm.includes(':'))
    ? hhmm.split(':').map((n) => Number(n) || 0)
    : [0, 0];
  const pad = (n) => String(n).padStart(2, '0');

  let y, mo, d;
  if (typeof date === 'string') {
    // Expect 'yyyy-MM-dd' or 'yyyy-MM-ddTHH:mm'
    const mDate = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (mDate) {
      y = Number(mDate[1]);
      mo = Number(mDate[2]);
      d = Number(mDate[3]);
    } else {
      // Fallback: derive Y-M-D in clinic TZ from Date object
      const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit'
      });
      const [yy, mm, dd] = fmt.format(new Date(date)).split('-');
      y = Number(yy); mo = Number(mm); d = Number(dd);
    }
  } else {
    // Date-like: derive Y-M-D in clinic TZ to avoid local machine TZ drift
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
    const [yy, mm, dd] = fmt.format(new Date(date)).split('-');
    y = Number(yy); mo = Number(mm); d = Number(dd);
  }

  const localStr = `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(m)}:00`;
  return zonedTimeToUtc(localStr, tz);
}

export function minutesDiff(a, b) {
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 60000);
}

export function formatTimeLocal(d, tz) {
  const date = d instanceof Date ? d : new Date(d);
  // Format in clinic timezone to avoid client-machine TZ differences
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: tz,
  }).format(date);
}

export function startOfDayLocal(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDayLocal(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function sameDay(a, b) {
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}

export function clamp(val, min, max) { return Math.max(min, Math.min(max, val)); }

export function snap30Minutes(date) {
  const d = new Date(date);
  const m = d.getMinutes();
  const rem = m % 30;
  if (rem >= 15) d.setMinutes(m + (30 - rem)); else d.setMinutes(m - rem);
  d.setSeconds(0, 0);
  return d;
}

export function toLocalDateTimeInputValue(dateLike, tz) {
  // Convert UTC instant to clinic-local components for input value
  const zoned = utcToZonedTime(new Date(dateLike), tz);
  const d = new Date(zoned);
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

// Parses 'yyyy-MM-dd HH:mm:ss' or 'yyyy-MM-ddTHH:mm:ss' as LOCAL time (no timezone conversion)
// Also supports seconds omitted and handles '24:00:00' by rolling to next day 00:00:00
export function parseLocalDateTimeString(s, tz) {
  if (!s) return null;
  if (s instanceof Date) return s;
  if (typeof s !== 'string') return new Date(s);
  try {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) {
      // Fallback to native Date
      return new Date(s);
    }
  const [, ys, ms, ds, hs, mins, secs] = m;
  let y = Number(ys), mo = Number(ms) - 1, d = Number(ds);
    let h = Number(hs), mi = Number(mins), se = Number(secs || '0');
    if (h === 24) {
      // roll to next day at 00:00:00
      h = 0; mi = 0; se = 0;
      // advance date by 1
      const base = new Date(y, mo, d);
      base.setDate(base.getDate() + 1);
      y = base.getFullYear(); mo = base.getMonth(); d = base.getDate();
    }
    const pad = (n) => String(n).padStart(2, '0');
    const localStr = `${y}-${pad(mo + 1)}-${pad(d)}T${pad(h)}:${pad(mi)}:${pad(se)}`;
    // Interpret as clinic-local time and convert to UTC instant
    return zonedTimeToUtc(localStr, tz);
  } catch {
    return new Date(s);
  }
}

export function toDateInputValueInTimeZone(dateLike, tz) {
  if (!tz) {
    throw new Error('Timezone is required to convert to local date input value');
  }
  const zoned = utcToZonedTime(new Date(dateLike), tz);
  const d = new Date(zoned);
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  return `${yyyy}-${mm}-${dd}`;
}

// Temporary backward-compatible alias
/** @deprecated Use toDateInputValueInTimeZone */
export const toLocalDateInputValue = toDateInputValueInTimeZone;

export function formatMMDD(d, tz) {
  const date = new Date(d);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${mm}/${dd}`;
}

export function dateOnly(date, preferences) {
  const d = toLocalDateTimeInputValue(date, preferences.timezone);
  return formatDateWithPreferences(d, preferences);
}

export function timeOnly(date, preferences) {
  const d = toLocalDateTimeInputValue(date, preferences.timezone);
  return formatTimeWithPreferences(d, preferences);
}